/**
 * TrialBanner — shown across the app while the user is on a 14-day trial.
 * Disappears the moment plan_tier flips to basic/growth (post-Stripe charge)
 * or back to free (cancellation).
 *
 * Days-remaining countdown + a CTA. The CTA differs based on signup flow:
 *   - self_serve: "Upgrade now" (skip trial, charge today) → opens billing portal
 *   - demo_token: "Add card to keep using" → opens billing portal where they
 *     can change plan to a paid one
 *
 * Also escalates color/tone in the last 48h.
 */

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useProfile } from '../../hooks/useProfile'

export function TrialBanner() {
  const { t } = useTranslation('common')
  const { data: profile } = useProfile()

  if (!profile || profile.plan_tier !== 'trial' || !profile.trial_ends_at) {
    return null
  }

  const endsAt = new Date(profile.trial_ends_at)
  const now = new Date()
  const msRemaining = endsAt.getTime() - now.getTime()
  const ONE_DAY = 1000 * 60 * 60 * 24
  // 2-bucket display: only show whole days when >= 1 full day remains.
  // Below that we say "less than a day" — saying "1 day left" when 90
  // minutes remain is misleading.
  const fullDaysRemaining = Math.max(0, Math.floor(msRemaining / ONE_DAY))
  const lessThanADay = msRemaining > 0 && msRemaining < ONE_DAY

  const expired = msRemaining <= 0
  // Urgent = last 48h (including <1d)
  const urgent = !expired && (lessThanADay || fullDaysRemaining <= 1)

  const tone = expired
    ? 'border-red-200 bg-red-50 text-red-900'
    : urgent
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-indigo-200 bg-indigo-50 text-indigo-900'

  const message = expired
    ? t('trialBanner.expired', { defaultValue: 'Your trial has ended. Add or update your card to keep sending.' })
    : lessThanADay
      ? t('trialBanner.lessThanADay', { defaultValue: 'Less than a day left in your trial. Upgrade to keep sending.' })
      : fullDaysRemaining === 1
        ? t('trialBanner.lastDay', { defaultValue: 'Last day of your trial. Upgrade to keep sending.' })
        : t('trialBanner.daysLeft', {
            defaultValue: '{{days}} days left in your free trial',
            days: fullDaysRemaining,
          })

  // Text badge for the urgent/expired states — emoji alone reads as
  // decoration. The non-urgent state keeps the friendly emoji only.
  const badge = expired
    ? t('trialBanner.expiredBadge', { defaultValue: 'EXPIRED' })
    : (urgent && (lessThanADay || fullDaysRemaining === 1))
      ? t('trialBanner.lastDayBadge', { defaultValue: 'LAST DAY' })
      : null

  return (
    <div className={`flex items-center justify-between gap-3 border-b px-4 py-2 text-xs sm:text-sm ${tone}`}>
      <div className="flex items-center gap-2 truncate">
        {badge ? (
          <span className="shrink-0 rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
            {badge}
          </span>
        ) : (
          <span aria-hidden="true">🎁</span>
        )}
        <span className="truncate font-medium">{message}</span>
      </div>
      <Link
        to="/settings"
        className="shrink-0 rounded-md border border-current bg-white/60 px-2.5 py-1 text-xs font-semibold hover:bg-white"
      >
        {t('trialBanner.cta', { defaultValue: 'Manage billing' })}
      </Link>
    </div>
  )
}
